import { X, Inbox } from 'lucide-react';
import {
  useEffect,
  useRef,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type ReactNode,
} from 'react';
import s from './ui.module.css';

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

// ── Card ──────────────────────────────────────────────────────────────

type CardProps = HTMLAttributes<HTMLDivElement> & {
  accent?: boolean;
  compact?: boolean;
};

export function Card({ accent, compact, className, children, ...rest }: CardProps) {
  return (
    <div className={cx(s.card, compact && s.cardSm, accent && s.cardAccent, className)} {...rest}>
      {children}
    </div>
  );
}

export function CardTitle({ children, sub }: { children: ReactNode; sub?: ReactNode }) {
  return (
    <div>
      <div className={s.cardTitle}>{children}</div>
      {sub ? <div className={s.cardSub}>{sub}</div> : null}
    </div>
  );
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return <div className="eyebrow">{children}</div>;
}

// ── Button ────────────────────────────────────────────────────────────

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'ghost' | 'quiet' | 'done' | 'danger';
  size?: 'sm' | 'md';
};

export function Button({
  variant = 'quiet',
  size = 'md',
  className,
  children,
  ...rest
}: ButtonProps) {
  const variantClass = {
    primary: s.btnPrimary,
    ghost: s.btnGhost,
    quiet: s.btnQuiet,
    done: s.btnDone,
    danger: s.btnDanger,
  }[variant];

  return (
    <button
      type="button"
      className={cx(s.btn, variantClass, size === 'sm' && s.btnSm, className)}
      {...rest}
    >
      {children}
    </button>
  );
}

// ── Segmented control ─────────────────────────────────────────────────

type SegmentedProps<T extends string> = {
  value: T;
  options: Array<{ value: T; label: ReactNode }>;
  onChange: (value: T) => void;
  mono?: boolean;
  ariaLabel: string;
};

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  mono,
  ariaLabel,
}: SegmentedProps<T>) {
  return (
    <div className={s.segmented} role="tablist" aria-label={ariaLabel}>
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={active}
            className={cx(
              s.segment,
              mono && s.segmentMono,
              active && s.segmentActive,
              active && mono && s.segmentMonoActive,
            )}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Fields ────────────────────────────────────────────────────────────

/** The small uppercase mono field label used throughout the design. */
export function Label({ htmlFor, children }: { htmlFor?: string; children: ReactNode }) {
  return (
    <label className={s.label} htmlFor={htmlFor}>
      {children}
    </label>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className={s.label}>{label}</span>
        {hint ? (
          <span
            style={{
              marginLeft: 'auto',
              fontSize: 11.5,
              color: 'var(--accent-text)',
              marginBottom: 7,
            }}
          >
            {hint}
          </span>
        ) : null}
      </div>
      {children}
    </div>
  );
}

export const fieldStyles = {
  input: s.input,
  inputMono: cx(s.input, s.inputMono),
  /** `input[type="time"]` — see `.inputTime`, which stops iOS overflowing it. */
  inputTime: cx(s.input, s.inputMono, s.inputTime),
  select: s.select,
  textarea: cx(s.input, s.textarea),
  range: s.range,
  rangeLabels: s.rangeLabels,
};

export function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className={cx(s.toggle, checked && s.toggleOn)}
      onClick={() => onChange(!checked)}
    >
      <span className={s.toggleKnob} />
    </button>
  );
}

// ── Tag ───────────────────────────────────────────────────────────────

export function Tag({
  children,
  tone = 'accent',
}: {
  children: ReactNode;
  tone?: 'accent' | 'warn' | 'neutral';
}) {
  return (
    <span className={cx(s.tag, tone === 'warn' && s.tagWarn, tone === 'neutral' && s.tagNeutral)}>
      {children}
    </span>
  );
}

// ── Progress ──────────────────────────────────────────────────────────

export function Bar({ pct, color, tall }: { pct: number; color: string; tall?: boolean }) {
  return (
    <div className={cx(s.track, tall && s.trackTall)}>
      <div
        className={s.fill}
        style={{ width: `${Math.max(0, Math.min(100, pct))}%`, background: color }}
      />
    </div>
  );
}

export function Dot({ lime, pulse }: { lime?: boolean; pulse?: boolean }) {
  return <span className={cx(s.dot, lime && s.dotLime, pulse && s.dotPulse)} />;
}

// ── States ────────────────────────────────────────────────────────────

export function Empty({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className={s.empty}>
      <div className={s.emptyMark}>
        <Inbox size={22} aria-hidden="true" />
      </div>
      <div>{children}</div>
      {action}
    </div>
  );
}

export function Skeleton({
  height = 20,
  width = '100%',
}: {
  height?: number | string;
  width?: number | string;
}) {
  return <div className={s.skeleton} style={{ height, width }} />;
}

export function ErrorBox({ children }: { children: ReactNode }) {
  return <div className={s.errorBox}>{children}</div>;
}

export function Spinner() {
  return <span className={s.spinner} aria-hidden="true" />;
}

/** Full-card loading state, sized to roughly match the content it replaces. */
export function CardSkeleton({ lines = 4, height = 200 }: { lines?: number; height?: number }) {
  return (
    <Card style={{ minHeight: height, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Skeleton height={14} width="40%" />
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} height={12} width={`${90 - i * 12}%`} />
      ))}
    </Card>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────

const openModalPanels: HTMLElement[] = [];
let bodyOverflowBeforeModals = '';

export function Modal({
  title,
  onClose,
  children,
  className,
}: {
  title: string;
  className?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Install once: parent state changes must not steal focus while typing.
  // Only the top dialog handles keys, and nested dialogs share the scroll lock.
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const previous = document.activeElement as HTMLElement | null;
    if (!openModalPanels.length) bodyOverflowBeforeModals = document.body.style.overflow;
    openModalPanels.push(panel);
    document.body.style.overflow = 'hidden';
    panel.focus();
    const onKey = (event: KeyboardEvent) => {
      if (openModalPanels[openModalPanels.length - 1] !== panel) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(
        panel.querySelectorAll<HTMLElement>(
          'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => element.getClientRects().length > 0);
      const first = focusable[0],
        last = focusable[focusable.length - 1];
      if (!first) {
        event.preventDefault();
        panel.focus();
      } else if (
        event.shiftKey &&
        (document.activeElement === first || document.activeElement === panel)
      ) {
        event.preventDefault();
        last!.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === last || !panel.contains(document.activeElement))
      ) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const index = openModalPanels.indexOf(panel);
      if (index >= 0) openModalPanels.splice(index, 1);
      if (!openModalPanels.length) document.body.style.overflow = bodyOverflowBeforeModals;
      if (previous?.isConnected) previous.focus();
    };
  }, []);

  return (
    <div className={s.overlay} onClick={onClose} role="presentation">
      <div
        ref={panelRef}
        className={cx(s.modal, className)}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={s.modalScroll}>
          <div className={s.modalHeader}>
            <div className={s.modalTitle}>{title}</div>
            <button type="button" className={s.modalClose} onClick={onClose} aria-label="Close">
              <X size={20} aria-hidden="true" />
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

// ── Toast ─────────────────────────────────────────────────────────────

export function Toast({ message }: { message: string }) {
  return (
    <div className={s.toast} role="status" aria-live="polite">
      <Dot lime />
      <span>{message}</span>
    </div>
  );
}
